import React, { useMemo } from 'react'
import { KeyboardAvoidingView, StyleSheet } from 'react-native'

import { MobileNav } from 'partials/MobileNav'
import { NavMenuButton } from 'partials/MobileNav/types'

import { Banner } from 'widgets/Banner'
import { Box } from 'widgets/Box'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { TransparentOverlay } from 'widgets/TransparentOverlay'
import { UpspaceHelmet } from 'widgets/UpspaceHelmet'

import { ThemeColor } from 'utils/themes/types'

import { useFocusMode } from 'contexts/FocusModeContext'

import { LoadingOverlayContainer, MaxWidthContainerBox, StyledScrollView } from './styled'

interface PageProps {
  withScroll?: boolean
  withNav?: boolean
  withBack?: boolean
  withLogoButton?: boolean
  navigation?: any
  children?: any
  isLoading?: boolean
  center?: boolean
  title?: string
  backgroundColor?: ThemeColor
  withChatButton?: boolean
  navbarRightButtons?: NavMenuButton[]
  enableKeyboardAvoidingView?: boolean
  bannerText?: string | undefined
  bannerButtonText?: string
  bannerButtonOnPress?: () => void
  backgroundImageSource?: { uri?: string }
  withSearch?: boolean
  withExitFocusMode?: boolean
  withNotificationButton?: boolean
  withSearchButton?: boolean
  isOnBottomSheet?: boolean
  renderHelmet?: boolean
}

const styles = StyleSheet.create({
  focusModePageStyle: {
    overflow: 'hidden',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
})

export const Page = ({
  withScroll = false,
  withNav = false,
  center = false,
  withBack = false,
  withLogoButton = false,
  isLoading = false,
  title,
  navigation,
  children,
  backgroundColor,
  withChatButton,
  navbarRightButtons,
  enableKeyboardAvoidingView = true,
  bannerText,
  bannerButtonText,
  bannerButtonOnPress,
  backgroundImageSource,
  withSearch = false,
  withExitFocusMode = false,
  withNotificationButton = false,
  withSearchButton = false,
  isOnBottomSheet = false,
  renderHelmet = true,
}: PageProps) => {
  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])

  let content = (
    <Box
      flex={1}
      align='center'
      justifyContent={center ? 'center' : undefined}
      backgroundColor={backgroundColor}
    >
      <MaxWidthContainerBox>
        {withNav && (
          <MobileNav
            title={title}
            withBack={withBack}
            withLogoButton={withLogoButton}
            navigation={navigation}
            withChatButton={withChatButton}
            navbarRightButtons={navbarRightButtons}
            withSearch={withSearch}
            withExitFocusMode={withExitFocusMode}
            withNotificationButton={withNotificationButton}
            withSearchButton={withSearchButton}
          />
        )}
        {bannerText && (
          <Banner text={bannerText} buttonText={bannerButtonText} onPress={bannerButtonOnPress} />
        )}
        <Box flex={1} backgroundColor={isFocusMode && !isOnBottomSheet ? 'v2black' : 'white'}>
          <Box
            flex={1}
            backgroundColor={backgroundColor}
            style={isFocusMode ? styles.focusModePageStyle : undefined}
          >
            {children}
          </Box>
        </Box>
      </MaxWidthContainerBox>
    </Box>
  )

  if (withScroll) {
    content = <StyledScrollView backgroundColor={backgroundColor}>{content}</StyledScrollView>
  }

  if (backgroundImageSource) {
    content = <TransparentOverlay source={backgroundImageSource}>{content}</TransparentOverlay>
  }

  if (enableKeyboardAvoidingView) {
    content = (
      <KeyboardAvoidingView style={{ flex: 1 }} behavior='padding'>
        {content}
      </KeyboardAvoidingView>
    )
  }

  return (
    <>
      {isLoading && (
        <LoadingOverlayContainer
          backgroundColor='white'
          display='flex'
          justifyContent='center'
          align='center'
          zIndex={10}
        >
          <LoadingSpinner />
        </LoadingOverlayContainer>
      )}
      {renderHelmet && <UpspaceHelmet navigation={navigation} />}
      {content}
    </>
  )
}
